// frontend/src/data/zodiacClashInsights.js
import { isEnglishPresentation } from '../localization/presentationLanguage';
// Insights psicológicos para Zodiac Clash
// Objetivo: evitar que se sienta repetitivo.
// - Genera variaciones con plantillas + sinónimos
// - Evita repetir mensajes recientes (sessionStorage)
// - Permite “matices” según tipo de pregunta (poder/control/seduccion/vinculo/general)

// ============================================================
// 1) Base de rasgos por signo (semántica, no frases finales)
// ============================================================

const SIGN_TRAITS = {
  aries: {
    core: ['impulso', 'fuego', 'decisión rápida', 'desafío'],
    general: ['acción', 'valentía', 'inmediatez'],
    poder: ['confrontación', 'dominancia directa', 'competencia'],
    control: ['corte rápido', 'liderazgo', 'presión'],
    seduccion: ['energía frontal', 'osadía', 'juego físico'],
    vinculo: ['lealtad', 'protección', 'presencia']
  },
  tauro: {
    core: ['lo sensorial', 'seguridad', 'constancia', 'placer consciente'],
    general: ['estabilidad', 'paciencia', 'realismo'],
    poder: ['firmeza', 'resistencia', 'lo propio'],
    control: ['calma firme', 'límites', 'ritmo propio'],
    seduccion: ['presencia sostenida', 'detalle sensorial', 'atracción tranquila'],
    vinculo: ['cuidado', 'hogar', 'confianza']
  },
  geminis: {
    core: ['mente', 'curiosidad', 'juego', 'palabras'],
    general: ['intercambio', 'variedad', 'agilidad'],
    poder: ['ingenio', 'persuasión', 'doble lectura'],
    control: ['ambigüedad', 'preguntas', 'cambio de marco'],
    seduccion: ['coqueteo mental', 'humor', 'misterio ligero'],
    vinculo: ['complicidad', 'charla', 'movimiento']
  },
  cancer: {
    core: ['sensibilidad', 'intuición', 'protección', 'memoria'],
    general: ['empatía', 'cuidado', 'percepción'],
    poder: ['silencio que pesa', 'profundidad', 'marcar hogar'],
    control: ['lectura emocional', 'lazos', 'presencia'],
    seduccion: ['ternura magnética', 'mirada', 'contención'],
    vinculo: ['seguridad afectiva', 'refugio', 'fidelidad']
  },
  leo: {
    core: ['presencia', 'identidad', 'brillo', 'orgullo'],
    general: ['carisma', 'seguridad', 'autoridad'],
    poder: ['imponerse', 'liderar', 'dominar escena'],
    control: ['mirada', 'estatus', 'validación'],
    seduccion: ['magnetismo', 'confianza', 'atreverse'],
    vinculo: ['lealtad', 'corazón', 'orgullo compartido']
  },
  virgo: {
    core: ['detalle', 'coherencia', 'observación', 'precisión'],
    general: ['criterio', 'orden', 'claridad'],
    poder: ['control fino', 'competencia', 'autoridad silenciosa'],
    control: ['microseñales', 'estructura', 'límites'],
    seduccion: ['inteligencia práctica', 'cuidado', 'calma'],
    vinculo: ['servicio', 'constancia', 'mejorar juntos']
  },
  libra: {
    core: ['armonía', 'estética', 'equilibrio', 'encanto'],
    general: ['diplomacia', 'gracia', 'sintonía'],
    poder: ['seducción social', 'imán suave', 'elegancia'],
    control: ['balance', 'gestos', 'acuerdo'],
    seduccion: ['encanto', 'detalle fino', 'buen gusto'],
    vinculo: ['pareja', 'complicidad', 'justicia afectiva']
  },
  escorpio: {
    core: ['intensidad', 'misterio', 'profundidad', 'verdad'],
    general: ['radiografía', 'instinto', 'silencio'],
    poder: ['psicología', 'penetración', 'control emocional'],
    control: ['mirada', 'tensión', 'dominancia silenciosa'],
    seduccion: ['magnetismo oscuro', 'atracción', 'peligro'],
    vinculo: ['lealtad total', 'fusión', 'exclusividad']
  },
  sagitario: {
    core: ['expansión', 'libertad', 'aventura', 'verdad'],
    general: ['horizonte', 'optimismo', 'movimiento'],
    poder: ['franqueza', 'coraje', 'energía'],
    control: ['independencia', 'no pertenecer', 'salir'],
    seduccion: ['chispa', 'juego', 'promesa'],
    vinculo: ['crecer', 'viajar', 'aprender juntos']
  },
  capricornio: {
    core: ['estrategia', 'solidez', 'autoridad', 'estructura'],
    general: ['plan', 'calma', 'responsabilidad'],
    poder: ['control consciente', 'presión fría', 'estatus'],
    control: ['disciplina', 'límites', 'tiempo'],
    seduccion: ['seriedad magnética', 'seguridad', 'intención'],
    vinculo: ['compromiso', 'proyecto', 'lealtad']
  },
  acuario: {
    core: ['diferencia', 'mente', 'impredecible', 'libertad'],
    general: ['originalidad', 'distancia', 'visión'],
    poder: ['desapego', 'no convencional', 'independencia'],
    control: ['no necesitar', 'cambio', 'frialdad estratégica'],
    seduccion: ['rareza', 'chispa mental', 'rebeldía'],
    vinculo: ['amistad', 'espacio', 'futuro compartido']
  },
  piscis: {
    core: ['energía', 'símbolo', 'empatía', 'magia'],
    general: ['intuición', 'vibración', 'sensibilidad'],
    poder: ['encanto', 'misterio', 'profundidad emocional'],
    control: ['sutileza', 'sueño', 'absorber'],
    seduccion: ['magnetismo invisible', 'romance', 'fantasía'],
    vinculo: ['compasión', 'fusión', 'alma']
  }
};

// ============================================================
// 2) Plantillas (se combinan con rasgos) — evita repetición
// ============================================================

const TEMPLATES = {
  openers: [
    'No fue casualidad: ',
    'Esto revela un patrón tuyo: ',
    'Lectura psicológica: ',
    'Clave energética: ',
    'Verdad sutil: '
  ],
  verbs: [
    'te atrae',
    'te engancha',
    'te domina',
    'te convence',
    'te seduce'
  ],
  closers: [
    'Ahí está tu patrón. ✨',
    'Esa es tu firma energética. 🧿',
    'Ese es tu punto de atracción. ⚡',
    'Por eso te pasa una y otra vez. 🧠',
    'Eso explica lo que repetís sin darte cuenta. 🔥'
  ],
  microRewards: [
    'Sumaste un voto al ritual. 🗳️',
    'Otro paso en tu racha diaria. 🔥',
    'Buen instinto. Guardalo. 🧿',
    'Elegiste rápido: eso es poder. ⚔️'
  ]
};

const CATEGORY_HINTS = {
  general: { emoji: '✨', label: 'intuición' },
  poder: { emoji: '🖤', label: 'poder' },
  control: { emoji: '🧠', label: 'control' },
  seduccion: { emoji: '🔥', label: 'seducción' },
  vinculo: { emoji: '🫂', label: 'vínculo' }
};

// ============================================================
// 2.5) Helpers de redacción (evitar frases cortadas tipo “Te guía cuerpo”)
// ============================================================

function startsWithArticle(s) {
  return /^(el|la|los|las|un|una|unos|unas)\s+/i.test(String(s || '').trim());
}

function guessGenderArticle(noun) {
  const w = String(noun || '').trim().toLowerCase();
  // Heurística simple: terminaciones típicamente femeninas
  if (/(ción|sión|dad|tad|tud|sis|ez|ie|ia|anza|encia|idad|umbre)$/.test(w)) return 'la';
  // algunos casos comunes
  if (/^(mente|energía|intuición|presencia|sensualidad|constancia|seguridad|coherencia|observación|precisión|armonía|libertad|expansión|verdad|intensidad|profundidad|estrategia|autoridad|estructura|disciplina|lealtad|protección|empatía|magia)$/.test(w)) {
    return 'la';
  }
  return 'el';
}

function traitPhrase(trait) {
  const t = String(trait || '').trim();
  if (!t) return '';
  if (startsWithArticle(t)) return t;

  // Si parece una frase con sustantivo+adjetivo, igual agregamos artículo ("el/ la")
  // Ej: "decisión rápida", "energía frontal", "control fino"
  const art = guessGenderArticle(t);
  return `${art} ${t}`;
}


// ============================================================
// 3) Utilidades: normalización + anti-repetición
// ============================================================

export function normalizeSignKey(sign) {
  const s = String(sign || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  // soporta entradas como "cáncer"
  return s;
}

function safeSessionGet(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key, val) {
  try { sessionStorage.setItem(key, val); } catch {}
}

function pickFrom(list, rng) {
  if (!Array.isArray(list) || !list.length) return null;
  return list[Math.floor(rng() * list.length)];
}

// RNG determinístico liviano (evita repetir en el mismo clash)
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}
function mulberry32(a) {
  return function () {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function seededRand(seedStr) {
  const seed = xmur3(seedStr)();
  return mulberry32(seed);
}

function chooseCategory({ question, category }) {
  const c = normalizeSignKey(category);
  if (c && CATEGORY_HINTS[c]) return c;
  const q = String(question || '').toLowerCase();
  if (/control|domina|manipul|ignoro|obsesion|fr(i|í)a|psicol/i.test(q)) return 'control';
  if (/seduc|conquista|magnet|atrac|rompe el ego|tocar|carisma/i.test(q)) return 'seduccion';
  if (/poder|peligro|silencio|misterio|intensidad|verdad/i.test(q)) return 'poder';
  if (/vincul|protec|cuidado|segur|entreg|contenci/i.test(q)) return 'vinculo';
  return 'general';
}

function buildInsightSentence({ signKey, categoryKey, opponentKey, rng }) {
  const traits = SIGN_TRAITS[signKey] || null;
  const catList = traits?.[categoryKey] || traits?.general || [];
  const coreList = traits?.core || [];

  const t1raw = pickFrom(catList, rng) || pickFrom(coreList, rng) || 'intuición';
  const t2raw = pickFrom(coreList, rng) || pickFrom(catList, rng) || 'patrón';

  const t1 = traitPhrase(t1raw) || 'tu intuición';
  const t2 = traitPhrase(t2raw) || 'tu patrón';

  const opener = pickFrom(TEMPLATES.openers, rng) || '';
  const verb = pickFrom(TEMPLATES.verbs, rng) || 'te atrae';
  const closer = pickFrom(TEMPLATES.closers, rng) || '';

  const hint = CATEGORY_HINTS[categoryKey] || CATEGORY_HINTS.general;

  const mentionOpponent = opponentKey && rng() > 0.55;
  const oppPart = mentionOpponent
    ? ` (y si el otro era ${opponentKey}, es porque comparás desde ${t2}).`
    : '.';

  // Redacción: evitar cortes y mantener fluidez
  // Ej: "te atrae el cuerpo", "te seduce la calma que manda"
  return `${opener}${hint.emoji} En tu modo ${hint.label}, ${verb} ${t1}${oppPart} ${closer}`
    .replace(/\s+/g, ' ')
    .replace(/\s+\./g, '.')
    .trim();
}

function avoidRecentRepeat(text, bucketKey) {
  const k = `zdx:clash:insight:${bucketKey}`;
  const raw = safeSessionGet(k);
  let last = [];
  try { last = raw ? JSON.parse(raw) : []; } catch { last = []; }

  if (Array.isArray(last) && last.includes(text)) return false;

  const next = Array.isArray(last) ? last.slice(-6) : [];
  next.push(text);
  safeSessionSet(k, JSON.stringify(next));
  return true;
}

// ============================================================
// 4) API pública: pickZodiacClashInsight (para tu screen)
// ============================================================

/**
 * pickZodiacClashInsight
 * Usalo POST-voto, para reforzar identidad sin volverse repetitivo.
 *
 * @param {Object} args
 * @param {string} args.pickedSign - signo elegido (A/B)
 * @param {string} [args.opponentSign] - el otro signo
 * @param {string} [args.question] - pregunta del clash
 * @param {string} [args.category] - category hint (general/poder/control/seduccion/vinculo)
 * @param {string} [args.dayKey]
 * @param {string} [args.clashId]
 * @returns {string}
 */
export function pickZodiacClashInsight({
  pickedSign,
  opponentSign,
  question,
  category,
  dayKey,
  clashId
} = {}) {
  if (isEnglishPresentation()) {
    const sign = String(pickedSign || 'your sign');
    const opponent = String(opponentSign || '').trim();
    return `${sign.charAt(0).toUpperCase()}${sign.slice(1)} reflects the instinct you trusted in this matchup${opponent ? ` against ${opponent.charAt(0).toUpperCase()}${opponent.slice(1)}` : ''}. Your choice reveals how you read energy under pressure. ✨`;
  }
  const signKey = normalizeSignKey(pickedSign);
  const oppKey = opponentSign ? normalizeSignKey(opponentSign) : null;
  const catKey = chooseCategory({ question, category });

  // Seed: estable por día+clash+signo para que no cambie al rerender
  const seed = `zdx:clash:${dayKey || 'today'}:${clashId || 'x'}:${signKey}:${catKey}`;
  const rng = seededRand(seed);

  // Intentamos hasta 6 variaciones evitando repetición reciente
  const bucket = `${signKey}:${catKey}`;
  for (let i = 0; i < 6; i++) {
    const text = buildInsightSentence({ signKey, categoryKey: catKey, opponentKey: oppKey, rng });
    if (avoidRecentRepeat(text, bucket)) return text;
  }

  // Fallback (no debería llegar)
  return buildInsightSentence({ signKey, categoryKey: catKey, opponentKey: oppKey, rng });
}

/** Micro-reward opcional (para debajo del insight) */
export function pickZodiacClashMicroReward({ dayKey, clashId } = {}) {
  if (isEnglishPresentation()) return 'Good instinct. Keep reading the cosmic pattern. ⚡';
  const rng = seededRand(`zdx:clash:reward:${dayKey || 'today'}:${clashId || 'x'}`);
  return pickFrom(TEMPLATES.microRewards, rng) || 'Buen instinto. ⚡';
}

// ============================================================
// 5) Compatibilidad hacia atrás: export legacy object
// (si ya lo estabas usando en otro lado)
// ============================================================

export const ZODIAC_CLASH_INSIGHTS = Object.keys(SIGN_TRAITS).reduce((acc, k) => {
  // para no romper imports viejos: exponemos “general” como frases simples (pero bien redactadas)
  const t = SIGN_TRAITS[k] || {};
  const c0 = t.core?.[0] || 'intuición';
  const c1 = t.core?.[1] || 'lo sutil';
  const g0 = t.general?.[0] || 'energía';

  const p0 = traitPhrase(c0) || 'tu intuición';
  const p1 = traitPhrase(c1) || 'lo sutil';
  const p2 = traitPhrase(g0) || 'tu energía';

  acc[k] = {
    general: [
      `Tu brújula es ${p0} y se nota.`,
      `Tu patrón se activa con ${p1}.`,
      `Elegís desde ${p2}, sin pedir permiso.`
    ]
  };
  return acc;
}, {});

// Exporte útil para UI (categorías válidas)
export const ZODIAC_CLASH_CATEGORIES = Object.keys(CATEGORY_HINTS);
